import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

import ConfigurableDataTable from 'app/admin/common/component/ConfigurableDataTable';
import { Button } from './Pipeline';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

function safeDate(v) {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function formatTs(v) {
    const d = safeDate(v);
    return d ? format(d, 'yyyy.MM.dd HH:mm:ss', { locale: hu }) : '-';
}

function toDisplayValue(value) {
    if (value === null || typeof value === 'undefined') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value || '-';

    try {
        return JSON.stringify(value);
    } catch (e) {
        return String(value);
    }
}

function flattenObjectRows(obj, groupLabel) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];

    return Object.entries(obj).map(([key, value]) => ({
        group: groupLabel,
        key,
        value: toDisplayValue(value)
    }));
}

function buildRows(data) {
    if (!data) return [];

    const alarmVariablesCount = data.alarm_variables && typeof data.alarm_variables === 'object'
        ? Object.keys(data.alarm_variables).length
        : 0;

    const generalRows = [
        { group: 'General', key: 'Dynamic check name', value: toDisplayValue(data.edt_dynamic_check_name) },
        { group: 'General', key: 'Timestamp', value: formatTs(data.ts) },
        { group: 'General', key: 'Dynamic check ok', value: data.finished === true && data.converged === true && data.alarm === false ? 'Yes' : 'No' },
        { group: 'General', key: 'Base case succeeded', value: data.converged === true ? 'Yes' : 'No' },
        { group: 'General', key: 'Best case succeeded', value: data.finished === true ? 'Yes' : 'No' },
        { group: 'General', key: 'Finished', value: data.finished === true ? 'Yes' : 'No' },
        { group: 'General', key: 'Converged', value: data.converged === true ? 'Yes' : 'No' },
        { group: 'General', key: 'Alarm', value: data.alarm === true ? 'Yes' : 'No' },
        { group: 'General', key: 'State', value: toDisplayValue(data.state) },
        { group: 'General', key: 'Alarm variables count', value: String(alarmVariablesCount) }
    ];

    return [
        ...generalRows,
        ...flattenObjectRows(data.base_disturbance_variables, 'Base disturbance variables'),
        ...flattenObjectRows(data.base_set_points, 'Base set points'),
        ...flattenObjectRows(data.best_disturbance_variables, 'Best disturbance variables'),
        ...flattenObjectRows(data.best_set_points, 'Best set points'),
        ...flattenObjectRows(data.variables_to_monitor, 'Variables to monitor'),
        ...flattenObjectRows(data.alarm_variables, 'Alarm variables'),
        ...flattenObjectRows(data.sequence_finished_tags, 'Sequence finished tags')
    ];
}

export default function DynamicValidationDetailsPopup({ data }) {
    const { popup, setPopup } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);

    const rows = useMemo(() => buildRows(data), [data]);

    const columns = useMemo(() => {
        return [
            { key: 'group', header: 'Group', sortable: true },
            { key: 'key', header: 'Key', sortable: true },
            { key: 'value', header: 'Value', sortable: true }
        ];
    }, []);

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open={popup === 'DYNAMIC_VALIDATION_DETAILS'} fullWidth maxWidth={rows.length ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className='flex items-center justify-between gap-32 px-32'>
                        <div className='whitespace-nowrap'>EDT Dynamic Check - Details</div>
                        <Button label='Close' onClick={() => setPopup(null)} />
                    </div>
                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: rows.length ? '520px' : 'auto' }}>
                    <ConfigurableDataTable rows={rows} columns={columns} searchable={false} />
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}